import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import {Add, Refresh, Delete, SupervisedUserCircle} from '@material-ui/icons';
import { BackButton } from '../../../sc-utilities';
import MaterialTable from 'material-table';
import { hasPermission, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../../sc-utilities';
import UserDelete from './UserDelete';
import * as actions from './store/userActions';
import * as action from '../../../np-core/Auth/store/authActions';
import Toolbar from '../../../sc-utilities/Toolbar'
import { MuiTableRows } from '../../../../themes';
import { CancelToken } from 'axios';
import ChangeGroupModal from "./ChangeGroupModal";
import {fetchBulkChangeUsersGroup} from "../../../sc-group/Group/store/groupActions";

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const UserList = props => {
    const { classes, translations, permissions, onFetchUsersCount, onFetchUsers, fetching, saveColumnsFilter, currentUserID, activeCustomerID, onBulkDeleteUsers, tableColumns, redirectHandler, tableRef, groups, onBulkChangeUserGroup } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, userIDs: [] });
    const [changeGroupModalState, setChangeGroupModalState] = useState({ open: false, userIDs: [] });

    const columnObject = useMemo(() => tableColumns, [tableColumns]);

    const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    const selectionPermissions = hasAtLeastOnePermission(permissions, ['user_delete', 'user_change_group']);
    const deletePermission = hasPermission(permissions, 'user_delete');

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        filterCellStyle: { maxWidth: '150px' },
        selectionProps: rowData => {
            let disabled = false;
            if (deletePermission) {
                if (rowData.UserID === currentUserID) {
                    disabled = true;
                }
            }
            rowData.tableData.disabled = disabled;
            return disabled ? {
                disabled: true,
                id: `checkbox_${rowData.UserID}`,
                checked: false
            } : { id: `checkbox_${rowData.UserID}` }
        },
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchUsersCount(query, { countSource: countSource });
        if (!query['page'] && !rowsPageSizeInit.includes(query['pageSize'])) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count.data ? await onFetchUsers(query, { listSource: listSource }) : { data: [] };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['user'] || 'User'}</Typography>
                {hasPermission(permissions, 'user_create') ? <Fab
                    id="create_user"
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`/users/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, userIDs: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteUsers({ UserIDs: deleteModalState.userIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, userIDs: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = userIDs => {
        setDeleteModalState({ open: true, userIDs: userIDs });
    };

    const handleBulkChangeUsersGroup = userIDs => {
        setChangeGroupModalState({ open: true, userIDs: userIDs });
    };

    const handleChangeGroupCloseSafe = () => {
        setChangeGroupModalState({ open: false });
    };

    const handleChangeGroupCloseDanger = async (GroupID) => {
        await onBulkChangeUserGroup({ UserIDs: changeGroupModalState.userIDs, ActiveCustomerID: activeCustomerID, GroupID });
        setChangeGroupModalState({ open: false, userIDs: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["users"] || "Users"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(user => user.UserID))
        },
        hasPermission(permissions, 'user_change_group') ?{
            tooltip: translations["change_group"] || "Change group",
            icon: <SupervisedUserCircle />,
            onClick: (rowData) => handleBulkChangeUsersGroup(rowData.map(user => user.UserID))
        } : {}
    ];

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`User`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID });
        return newColumnState;
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
                onChangeColumnHidden={columnHidden}
            />
            {hasPermission(permissions, 'user_delete') ? (
                <UserDelete
                    entityName={translations['user'] || 'User'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}

            {hasPermission(permissions, 'user_change_group') ? (
                <ChangeGroupModal
                    groups={groups}
                    entityName={translations['users'] || 'Users'}
                    translations={translations}
                    handleCloseDanger={handleChangeGroupCloseDanger}
                    handleCloseSafe={handleChangeGroupCloseSafe}
                    modalState={changeGroupModalState}
                    permissions={permissions}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        fetching: state.user.users.fetching,
        currentUserID: state.core.login.user.UserID
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchUsersCount: (filter, options) => dispatch(actions.fetchUsersCount(filter, options)),
        onFetchUsers: (filter, options) => dispatch(actions.fetchUsers(filter, options)),
        onBulkDeleteUsers: user => dispatch(actions.bulkDeleteUser(user)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onBulkChangeUserGroup: data => dispatch(fetchBulkChangeUsersGroup(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(UserList));